import { Card, Col, Descriptions, Divider, Row, Tag } from 'antd';
import { map, slice } from 'lodash';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useMedia } from 'react-use';
import { CloseIcon, InfoIconDark } from '../../assets/svg';
import {
  BREAKPOINTS,
  DATE_TIME_WITH_AT,
  DATETIMEWITHDIVIDE,
  REGISTER_LOG_STATUS,
  STAGE_STATUS_KEYS,
} from '../../common/constants';
import { titleCase } from '../../common/utils';
import CommonPopover from '../../components/CommonPopover';
import CommonTooltip from '../../components/CommonTooltip';
import EllipsisText from '../../components/EllipsisText';

const RegisterApprovalSummary = ({ data, loading }) => {
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const isTabletViewport = useMedia(`(min-width: ${BREAKPOINTS.tablet}px)`);
  const [openApproverPopover, setOpenApproverPopover] = useState(false);

  const registerLog = data?.registerLog;
  const approvalLevels = data?.unifiedApprovalHistory || [];

  const stageStatusTags = (status) => {
    switch (status) {
      case STAGE_STATUS_KEYS.APPROVED:
        return (
          <Tag className="active-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVED)}
          </Tag>
        );
      case STAGE_STATUS_KEYS.APPROVAL_PENDING:
        return (
          <Tag className="yellow-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVAL_PENDING)}
          </Tag>
        );
      case STAGE_STATUS_KEYS.REDO:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REDO)}</Tag>;
      case STAGE_STATUS_KEYS.REJECTED:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REJECTED)}</Tag>;
      default:
        return null;
    }
  };

  const getApprovalHistoryContent = () => {
    return map(approvalLevels, (record, index) => {
      const approvers = record?.approvers || [];
      const hasApprovers = approvers?.length > 0;
      const isApprovalPending =
        record?.status === STAGE_STATUS_KEYS.APPROVAL_PENDING;

      return (
        <>
          <Card className="approvers-card">
            <Row justify="space-between" gutter={18}>
              <Col span={14}>
                <EllipsisText
                  className="text-secondary mb-15 level-name"
                  text={record?.levelName}
                />
                {!isApprovalPending && hasApprovers && (
                  <div>
                    By
                    {map(approvers, (approverName, approverIndex) => (
                      <Tag
                        key={approverIndex}
                        className="ml-5 approver-name-tag"
                      >
                        <EllipsisText text={approverName} />
                      </Tag>
                    ))}
                  </div>
                )}
              </Col>
              <Col span={10}>
                <div className="mb-15">{stageStatusTags(record?.status)}</div>
                {!isApprovalPending && (
                  <div className="font-size-12">
                    {moment(record?.createdAt)
                      ?.tz(data?.register?.project?.timeZone)
                      ?.format(DATE_TIME_WITH_AT)}
                  </div>
                )}
              </Col>
              {isApprovalPending && hasApprovers && (
                <Col span={24}>
                  <div>
                    from
                    <>
                      <Tag className="ml-5 approver-name-tag">
                        <EllipsisText text={approvers?.[0]} />
                      </Tag>
                      {approvers?.length > 1 && (
                        <CommonTooltip
                          title={slice(approvers, 1, approvers?.length).join(
                            ', ',
                          )}
                          getPopupContainer={() =>
                            // eslint-disable-next-line no-undef
                            document.querySelector('.eqc-summary') ||
                            // eslint-disable-next-line no-undef
                            document.body
                          }
                          placement={isDesktopViewport ? 'right' : 'top'}
                        >
                          {`and +${approvers?.length - 1}`}
                        </CommonTooltip>
                      )}
                    </>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
          {approvalLevels?.length - 1 > index && (
            <Divider className="approver-divider" dashed />
          )}
        </>
      );
    });
  };

  const logStatus = (status) => {
    switch (status) {
      case REGISTER_LOG_STATUS.APPROVED:
        return (
          <Tag className="active-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVED)}
          </Tag>
        );
      case REGISTER_LOG_STATUS.APPROVAL_PENDING:
        return (
          <Tag className="yellow-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVAL_PENDING)}
          </Tag>
        );
      case REGISTER_LOG_STATUS.REDO:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REDO)}</Tag>;
      case REGISTER_LOG_STATUS.REJECTED:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REJECTED)}</Tag>;
      default:
        return <Tag color="gray">{titleCase(status || 'Unknown')}</Tag>;
    }
  };

  const getStatusLabel = () => {
    return (
      <div className="d-flex align-center">
        <span>Status</span>
        {approvalLevels?.length > 0 && (
          <CommonPopover
            classNames={{
              root: 'approver-info',
              container: 'approver-popover-container',
            }}
            onOpenChange={(visible) => {
              setOpenApproverPopover(visible);
            }}
            autoAdjustOverflow={!isDesktopViewport}
            getPopupContainer={() =>
              // eslint-disable-next-line no-undef
              document.querySelector('.eqc-summary') || document.body
            }
            title={
              <Row
                justify="space-between"
                align="top"
                onClick={(e) => e?.stopPropagation()}
              >
                <Col span={20}>
                  <EllipsisText text="Approval History" />
                </Col>
                <Col span={2}>
                  <CloseIcon
                    height={24}
                    width={24}
                    className="pointer"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setOpenApproverPopover(false);
                    }}
                  />
                </Col>
              </Row>
            }
            trigger="click"
            placement="bottomLeft"
            destroyTooltipOnHide
            open={openApproverPopover}
            content={
              <div onClick={(e) => e?.stopPropagation()}>
                {getApprovalHistoryContent()}
              </div>
            }
          >
            <InfoIconDark
              height={18}
              width={18}
              className="approver-info-icon ml-5 pointer"
              onClick={(e) => {
                e?.stopPropagation();
              }}
            />
          </CommonPopover>
        )}
      </div>
    );
  };

  // Calculate column configuration based on viewport
  const getColumnConfig = () => {
    if (isDesktopViewport) {
      return { column: 12, itemSpan: 2 }; // 6 columns for desktop to fit all in one row
    }
    if (isTabletViewport) {
      return { column: 2, itemSpan: 1 };
    }
    return { column: 1, itemSpan: 1 };
  };

  const { column, itemSpan } = getColumnConfig();

  return (
    <div className="eqc-summary">
      <div
        className={
          isDesktopViewport
            ? 'd-flex justify-between align-center'
            : 'd-flex flex-vertical'
        }
      >
        <Card loading={loading} className="eqc-summary-card">
          <Descriptions
            column={column}
            layout="vertical"
            colon={false}
            className="register-approval-summary-descriptions"
          >
            <Descriptions.Item label="Register Name" span={itemSpan}>
              <EllipsisText text={data?.register?.name} />
            </Descriptions.Item>
            <Descriptions.Item label={getStatusLabel()} span={itemSpan}>
              {logStatus(data?.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Approval Level" span={itemSpan}>
              {data?.levelName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Log Created On" span={itemSpan}>
              {registerLog?.createdAt
                ? moment(registerLog?.createdAt)
                    ?.tz(data?.register?.project?.timeZone)
                    ?.format(DATETIMEWITHDIVIDE)
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Approval Requested By" span={itemSpan}>
              <EllipsisText text={registerLog?.createdByUser?.name || '-'} />
            </Descriptions.Item>
            <Descriptions.Item label="Approval Requested On" span={itemSpan}>
              {registerLog?.submittedForApprovalAt
                ? moment(registerLog?.submittedForApprovalAt)
                    ?.tz(data?.register?.project?.timeZone)
                    ?.format(DATETIMEWITHDIVIDE)
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>
    </div>
  );
};

export default RegisterApprovalSummary;
